import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import * as exportTemplatesService from '../services/exportTemplatesService';
import * as leadsService from '../services/leadsService';
import {
  EXPORT_COLUMN_OPTIONS,
  buildExportData,
  downloadCSV,
  downloadXLSX,
  downloadPDF,
  isLeadExportTemplate
} from '../utils/leadsExport';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiPlus, FiTrash2 } = FiIcons;

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'pdf', label: 'PDF' }
];

const LEADS_TEMPLATE_PREFIX = '[Leads] ';

const FORM_RESPONSE_COLUMNS = new Set(['form_responses', 'quiz_responses', 'survey_responses']);

export default function ExportLeadsModal({
  isOpen,
  onClose,
  leads = [],
  events = [],
  role,
  userId
}) {
  const [selectedColumns, setSelectedColumns] = useState(EXPORT_COLUMN_OPTIONS.map((c) => c.key));
  const [customFields, setCustomFields] = useState([]);
  const [format, setFormat] = useState('csv');
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const canSaveLoadTemplates = role === 'admin' || role === 'agent';

  const loadTemplates = useCallback(async () => {
    if (!canSaveLoadTemplates) return;
    setTemplatesLoading(true);
    const { data, error } = await exportTemplatesService.listExportTemplates({ role, userId });
    if (error) {
      setTemplates([]);
    } else {
      setTemplates((data ?? []).filter(isLeadExportTemplate));
    }
    setTemplatesLoading(false);
  }, [canSaveLoadTemplates, role, userId]);

  useEffect(() => {
    if (isOpen && canSaveLoadTemplates) {
      loadTemplates();
    }
  }, [isOpen, canSaveLoadTemplates, loadTemplates]);

  useEffect(() => {
    if (!isOpen) {
      setSaveError(null);
      setSaveSuccess(false);
      setExportError(null);
      setSelectedTemplateId('');
    }
  }, [isOpen]);

  const handleLoadTemplate = async (templateId) => {
    if (!templateId) {
      setSelectedTemplateId('');
      return;
    }
    setSelectedTemplateId(templateId);
    const { data, error } = await exportTemplatesService.getExportTemplateById(templateId, { role, userId });
    if (error) {
      setSaveError(error.message);
      return;
    }
    if (data) {
      setSelectedColumns(Array.isArray(data.columns) ? data.columns : []);
      setCustomFields(Array.isArray(data.custom_fields) ? data.custom_fields : []);
      setFormat((data.format || 'csv').toLowerCase());
    }
  };

  const handleSaveTemplate = async () => {
    const trimmed = templateName.trim() || 'Untitled template';
    const name = trimmed.startsWith(LEADS_TEMPLATE_PREFIX)
      ? trimmed
      : `${LEADS_TEMPLATE_PREFIX}${trimmed}`;
    setSaveError(null);
    setSaveSuccess(false);
    const { error } = await exportTemplatesService.createExportTemplate(
      { name, format, columns: selectedColumns, custom_fields: customFields },
      { role, userId }
    );
    if (error) {
      setSaveError(error.message);
      return;
    }
    setSaveSuccess(true);
    setTemplateName('');
    loadTemplates();
  };

  const toggleColumn = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { label: '', value: '' }]);
  };

  const updateCustomField = (index, field) => {
    setCustomFields((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), ...field };
      return next;
    });
  };

  const removeCustomField = (index) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchExportEnrichment = async (leadIds, columns) => {
    const auth = { role, userId };
    const enrichment = {
      activitiesByLeadId: {},
      formResponsesByLeadId: {},
      agentsById: {}
    };

    const needsActivities = columns.includes('activities');
    const needsFormResponses = columns.some((c) => FORM_RESPONSE_COLUMNS.has(c));
    const needsAgents = columns.includes('agent_name') || columns.includes('agent_email');

    const fetches = [];

    if (needsActivities) {
      fetches.push(
        leadsService.listLeadActivitiesByLeadIds(leadIds, auth).then(({ data, error }) => {
          if (error) throw error;
          enrichment.activitiesByLeadId = data ?? {};
        })
      );
    }

    if (needsFormResponses) {
      fetches.push(
        leadsService.listFormResponsesByLeadIds(leadIds, auth).then(({ data, error }) => {
          if (error) throw error;
          enrichment.formResponsesByLeadId = data ?? {};
        })
      );
    }

    if (needsAgents) {
      const agentIds = [
        ...new Set(
          leads
            .map((lead) => lead.agentId ?? lead.agent_id)
            .filter(Boolean)
        )
      ];
      fetches.push(
        leadsService.listAgentsForLeadsExport(agentIds, auth).then(({ data, error }) => {
          if (error) throw error;
          enrichment.agentsById = data ?? {};
        })
      );
    }

    await Promise.all(fetches);
    return enrichment;
  };

  const runExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const leadIds = leads.map((l) => l.id).filter(Boolean);
      const enrichment = await fetchExportEnrichment(leadIds, selectedColumns);

      const customWithLabels = customFields.map((f) => ({
        label: (f && f.label ? String(f.label).trim() : '') || 'Custom',
        value: f && f.value != null ? String(f.value) : ''
      }));

      const rows = buildExportData(
        leads,
        selectedColumns,
        customWithLabels,
        enrichment,
        events
      );

      const timestamp = new Date().toISOString().slice(0, 10);
      const baseName = `lead_pipeline_export_${timestamp}`;

      if (format === 'csv') {
        downloadCSV(rows, `${baseName}.csv`);
      } else if (format === 'xlsx') {
        await downloadXLSX(rows, `${baseName}.xlsx`);
      } else {
        await downloadPDF(rows, `${baseName}.pdf`, 'Lead Pipeline Export');
      }
      onClose();
    } catch (err) {
      setExportError(err && err.message ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Export Leads</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <p className="text-sm text-gray-600">
            Exporting {leads.length} lead{leads.length === 1 ? '' : 's'} from the current filtered view.
          </p>

          {canSaveLoadTemplates && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Load template</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleLoadTemplate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white"
              >
                <option value="">— Select a template —</option>
                {templatesLoading
                  ? (
                    <option disabled>Loading…</option>
                    )
                  : (
                    templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.format})
                      </option>
                    ))
                    )}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Columns to include</label>
            <div className="flex flex-wrap gap-3">
              {EXPORT_COLUMN_OPTIONS.map((opt) => (
                <label key={opt.key} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(opt.key)}
                    onChange={() => toggleColumn(opt.key)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Custom columns (optional)</label>
              <button
                type="button"
                onClick={addCustomField}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                Add column
              </button>
            </div>
            <div className="space-y-2">
              {customFields.map((field, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={field.label ?? ''}
                    onChange={(e) => updateCustomField(i, { label: e.target.value })}
                    placeholder="e.g. Signature, Company, Notes"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeCustomField(i)}
                    className="p-2 text-gray-500 hover:text-red-600"
                    aria-label="Remove"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Export format</label>
            <div className="flex gap-4">
              {FORMAT_OPTIONS.map((opt) => (
                <label key={opt.value} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="leadExportFormat"
                    value={opt.value}
                    checked={format === opt.value}
                    onChange={() => setFormat(opt.value)}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {canSaveLoadTemplates && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Save as template</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm font-medium"
                >
                  Save
                </button>
              </div>
              {saveSuccess && <p className="text-sm text-green-600 mt-1">Template saved.</p>}
              {saveError && <p className="text-sm text-red-600 mt-1">{saveError}</p>}
            </div>
          )}

          {exportError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{exportError}</div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={runExport}
            disabled={exporting || selectedColumns.length === 0}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
}
